import { observer } from 'mobx-react-lite';
import React, { CSSProperties } from 'react';
import { Switch, Route, useLocation, Redirect } from 'react-router-dom';
import { Await } from '../../components/Await';
import { StyleOverride } from '../../components/landingPage/StyleOverride/StyleOverride';
import { SanityObject, useSanity, useSSR } from '../../lib/landingPage';
import { Page, PageProps } from './Page';
import { TrainingPage } from './TrainingPage';
import { TRAINING_URL } from '../../lib/landingPage/training';
import { SupportChatFacade } from '../../bound/landingPage/SupportChatFacade/SupportChatFacade';
import { Spin } from '../../components/Spin';
import {
    BlogCategoryPage,
    BlogHomePage,
    BlogPostPage,
    BlogSearchPage,
} from '../../bound/landingPage/BlogPage';
import routes from './routes';
import {
    AludeNewsPage,
    AludeNewsPostPage,
} from '../../bound/landingPage/AludeNews';
import { useSEO } from '../../lib';
import { getMetaDescription, getPageTitle } from '../../lib/dom';

const PageResolved = observer(
    ({ page }: { page: SanityObject<PageProps> }): JSX.Element => {
        const ssrContext = useSSR();
        useSEO(
            page.pageTitle ?? getPageTitle(),
            page.metaDescription ?? getMetaDescription()
        );
        ssrContext.setOpenGraphData(
            page.pageTitle ?? getPageTitle(),
            page.metaDescription ?? getMetaDescription()
        );

        return <Page value={page} />;
    }
);

const AsyncPage = observer(({ slug }: { slug: string }): JSX.Element => {
    const sanity = useSanity();
    return (
        <Await
            pending={() => (
                <Spin
                    style={{ height: '300px' }}
                    className={'horizontal center-middle'}
                />
            )}
            views={{
                page: sanity.asyncPage(slug),
            }}
            resolved={PageResolved}
        />
    );
});

const AppResolved = observer(
    ({ pages }: { pages: SanityObject<PageProps>[] }): JSX.Element => {
        const home = pages.find(c => c.slug.current === 'home');
        const { pathname, search } = useLocation();

        return (
            <StyleOverride
                style={{ '--alude-page-bg-color': 'white' } as CSSProperties}>
                <>
                    <Switch>
                        <Redirect
                            from="/:url*(/+)"
                            to={{
                                pathname: pathname.slice(0, -1),
                                search: search,
                            }}
                        />
                        <Route
                            path={routes.blog.category.path}
                            component={BlogCategoryPage}
                        />
                        <Route
                            path={routes.blog.search.path}
                            component={BlogSearchPage}
                        />
                        <Route
                            path={routes.blog.news.post.path}
                            component={AludeNewsPostPage}
                        />
                        <Route
                            path={routes.blog.news.path}
                            component={AludeNewsPage}
                        />
                        <Route
                            path={routes.blog.post.path}
                            component={BlogPostPage}
                        />
                        <Route
                            path={routes.blog.path}
                            component={BlogHomePage}
                        />
                        {pages.map(p => (
                            <Route
                                path={`/${p.slug.current}`}
                                key={p.slug.current}>
                                <AsyncPage slug={p.slug.current} />
                            </Route>
                        ))}
                        <Route path={TRAINING_URL()} key="training">
                            <TrainingPage />
                        </Route>
                        {home && (
                            <Route>
                                <AsyncPage slug={'home'} />
                            </Route>
                        )}
                    </Switch>
                    <SupportChatFacade />
                </>
            </StyleOverride>
        );
    }
);

export const App = observer((): JSX.Element => {
    const sanity = useSanity();

    if (typeof window !== 'undefined') {
        window.onload = () => {
            sanity.asyncAllPages;
        };
    }

    return (
        <Await
            views={{
                pages: sanity.asyncApp,
            }}
            resolved={AppResolved}
        />
    );
});
